import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/**
 * Hooks for the /admin/audits tab — Tank Rental Audit oversight (list every
 * audit the driver PWA filed, retry failed QBO writes) plus driver-access
 * management (who can use the audit PWA).
 */

export type AuditStatus =
  | 'balanced'
  | 'adjusted'
  | 'invoice_failed'
  | 'pending_approval'
  | 'declined';

export interface AdminAuditRow {
  id: string;
  audit_number: number;
  doc_number: string;
  customer_id: string | null;
  /** QuickBooks customer — set on every audit since 2026-09-22 (an account with no portal row has only this). */
  qbo_customer_id: string | null;
  customer_name: string | null;
  driver_name: string | null;
  driver_email: string | null;
  counts: Array<{ label: string; system_count: number; counted: number; delta: number }>;
  has_adjustment: boolean;
  qbo_invoice_id: string | null;
  signed_name: string;
  signed_at: string;
  manager_name: string | null;
  approved_by_email: string | null;
  approved_at: string | null;
  photo_count: number;
  notes: string | null;
  status: AuditStatus;
  /** The last QuickBooks refusal for the CR-ADJ invoice; null once it books. */
  invoice_error: string | null;
  created_at: string;
}

export function useAdminCylinderAudits(status: AuditStatus | 'all') {
  return useQuery<AdminAuditRow[]>({
    queryKey: ['admin-cylinder-audits', status],
    queryFn: async () => {
      const qs = status === 'all' ? '' : `?status=${status}`;
      const res = await adminFetch<{ audits: AdminAuditRow[] }>(
        `admin-list-cylinder-audits${qs}`,
      );
      return res.audits ?? [];
    },
    staleTime: 30 * 1000,
  });
}

export function useDecideCylinderAudit() {
  const qc = useQueryClient();
  return useMutation<
    { status: string; qbo_invoice_id?: string; note: string },
    Error,
    {
      auditId: string;
      action: 'approve' | 'decline';
      notes?: string;
      /** Manager amendments (approve only): final counted value per bucket. */
      counts?: Array<{ label: string; counted: number }>;
    }
  >({
    mutationFn: ({ auditId, action, notes, counts }) =>
      adminFetch('admin-decide-cylinder-audit', {
        method: 'POST',
        body: JSON.stringify({ audit_id: auditId, action, notes, counts }),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-cylinder-audits'] });
      qc.invalidateQueries({ queryKey: ['audit-document', `id=${vars.auditId}`] });
    },
  });
}

/** Bill the replacement fee for the tanks a decided audit found missing. */
export function useChargeMissingCylinders() {
  const qc = useQueryClient();
  return useMutation<
    { qbo_invoice_id: string; doc_number: string; tanks: number; rate: number; amount: number; note: string; relinked?: boolean },
    Error,
    { auditId: string; tanks?: number; notes?: string }
  >({
    mutationFn: ({ auditId, tanks, notes }) =>
      adminFetch('admin-charge-missing-cylinders', {
        method: 'POST',
        body: JSON.stringify({ audit_id: auditId, tanks, notes }),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-cylinder-audits'] });
      qc.invalidateQueries({ queryKey: ['audit-document', `id=${vars.auditId}`] });
    },
  });
}

export function useRetryCylinderAudit() {
  const qc = useQueryClient();
  return useMutation<
    { relinked: boolean; qbo_invoice_id: string; note: string },
    Error,
    string
  >({
    mutationFn: (auditId) =>
      adminFetch('admin-retry-cylinder-audit', {
        method: 'POST',
        body: JSON.stringify({ audit_id: auditId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-cylinder-audits'] });
    },
  });
}

export interface DriverRow {
  id: string;
  full_name: string;
  email: string | null;
  created_at: string;
}

export function useDrivers() {
  return useQuery<DriverRow[]>({
    queryKey: ['admin-drivers'],
    queryFn: async () => {
      const res = await adminFetch<{ drivers: DriverRow[] }>('admin-set-driver');
      return res.drivers ?? [];
    },
    staleTime: 60 * 1000,
  });
}

export function useSetDriver() {
  const qc = useQueryClient();
  return useMutation<
    { user_id?: string; is_driver?: boolean; note?: string },
    Error,
    { email: string; enable: boolean; name?: string }
  >({
    mutationFn: (input) =>
      adminFetch('admin-set-driver', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-drivers'] });
    },
  });
}
